import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock, Search, Tag } from 'lucide-react';
import { SEO, SchemaMarkup } from '../components/seo';
import { mergeArticles } from '../lib/article-input';
import { fetchApiArticles } from '../lib/articles-api';
import {
  ARTICLE_FILTERS,
  ARTICLES,
  filterArticles,
  isArticleFilterId,
  matchesArticleQuery,
  type Article,
  type ArticleFilterId,
} from '../lib/articles';

const FEATURED_ARTICLE = {
  tag: 'Werkgever' as const,
  title: 'Interne vs. externe vertrouwenspersoon: de complete vergelijking',
  desc: 'Een externe vertrouwenspersoon is vrijwel altijd én goedkoper én beter dan een interne. Bekijk de rekensom, de vergelijkingstabel en wat de wet echt verplicht.',
  to: '/interne-vs-externe-vp',
};

export function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('categorie');
  const urlQuery = searchParams.get('q') ?? '';
  const activeFilter: ArticleFilterId = isArticleFilterId(categoryParam) ? categoryParam : 'alle';
  const [query, setQuery] = useState(urlQuery);
  const [apiArticles, setApiArticles] = useState<Article[]>([]);
  const activeTag = ARTICLE_FILTERS.find((filter) => filter.id === activeFilter)?.tag ?? null;

  useEffect(() => {
    setQuery(urlQuery);
  }, [urlQuery]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearchParams((prev) => {
        const currentUrlQuery = prev.get('q') ?? '';
        if (query === currentUrlQuery) return prev;

        const params = new URLSearchParams(prev);
        if (!query.trim()) {
          params.delete('q');
        } else {
          params.set('q', query);
        }
        return params;
      }, { replace: true });
    }, 200);

    return () => window.clearTimeout(timeoutId);
  }, [query, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    fetchApiArticles().then((articles) => {
      if (!cancelled) setApiArticles(articles);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const articles = useMemo(() => mergeArticles(ARTICLES, apiArticles), [apiArticles]);
  const visibleArticles = useMemo(
    () => filterArticles(articles, activeFilter, query),
    [articles, activeFilter, query],
  );

  const showFeatured =
    (!activeTag || activeTag === FEATURED_ARTICLE.tag) &&
    matchesArticleQuery(FEATURED_ARTICLE, query);

  const hasResults = showFeatured || visibleArticles.length > 0;

  function setCategory(categorie: ArticleFilterId) {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (categorie === 'alle') {
        params.delete('categorie');
      } else {
        params.set('categorie', categorie);
      }
      if (!query.trim()) {
        params.delete('q');
      } else {
        params.set('q', query);
      }
      return params;
    });
  }

  function resetFilters() {
    setQuery('');
    setSearchParams({}, { replace: true });
  }

  return (
    <>
      <SEO
        title="Kennisbank | PuntUit"
        description="Kennisbank over de vertrouwenspersoon: artikelen over wetgeving, verplichtingen, kosten, interne vs. externe VP en preventie van ongewenst gedrag."
        canonicalUrl="/kennisbank"
      />
      <SchemaMarkup
        type="webpage"
        pageUrl="https://puntuit.nl/kennisbank"
        pageTitle="Kennisbank — PuntUit"
        pageDescription="Kennisbank over de vertrouwenspersoon: artikelen over wetgeving, verplichtingen, kosten, interne vs. externe VP en preventie van ongewenst gedrag."
        breadcrumb={[
          { name: 'Home', url: '/' },
          { name: 'Kennisbank', url: '/kennisbank' },
        ]}
      />
      <div className="pt-28">
        <section className="bg-[#0284c7] text-white">
          <div className="container py-24 md:py-32 max-w-4xl">
            <p className="text-xs font-semibold text-white/60 uppercase tracking-[0.2em] mb-6">Kennisbank</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Alles wat je moet weten over de vertrouwenspersoon.</h1>
            <p className="text-xl text-white/85">Artikelen voor werkgevers, HR-professionals en medewerkers.</p>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container max-w-5xl">
            <div
              className="flex flex-wrap gap-2 mb-6"
              role="group"
              aria-label="Filter artikelen op categorie"
            >
              {ARTICLE_FILTERS.map((filter) => {
                const isActive = filter.id === activeFilter;
                return (
                  <button
                    key={filter.id}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setCategory(filter.id)}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border cursor-pointer transition-colors ${
                      isActive
                        ? 'bg-[#0284c7] text-white border-[#0284c7]'
                        : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-[#0284c7]/5 hover:text-[#0284c7] hover:border-[#0284c7]/20'
                    }`}
                  >
                    <Tag className="w-3 h-3 mr-1.5" />
                    {filter.label}
                  </button>
                );
              })}
            </div>
            <div className="relative mb-10">
              <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label="Zoek een artikel"
                className="w-full border border-gray-300 rounded-xl pl-11 pr-4 py-3 text-sm"
                placeholder="Zoek een artikel..."
              />
            </div>

            {showFeatured && (
              <Link
                to={FEATURED_ARTICLE.to}
                className="block bg-sky-50 rounded-2xl border border-sky-100 p-7 mb-10 group hover:shadow-lg transition-all"
              >
                <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-[#0284c7]/10 text-[#0284c7] px-3 py-1.5 rounded-md mb-4">Uitgelicht</span>
                <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#0284c7] transition-colors leading-snug">{FEATURED_ARTICLE.title}</h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">{FEATURED_ARTICLE.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-[#0284c7] font-bold text-xs uppercase tracking-widest group-hover:gap-2.5 transition-all">
                  <BookOpen className="w-3.5 h-3.5" />
                  Bekijk de vergelijking
                  <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            )}

            {visibleArticles.length > 0 && (
              <div className="grid md:grid-cols-2 gap-6">
                {visibleArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/kennisbank/${article.slug}`}
                    className="bg-white rounded-2xl border border-gray-100 hover:shadow-lg transition-all group block overflow-hidden"
                  >
                    <div className="p-7">
                      <div className="flex items-center justify-between mb-4">
                        <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-[#0284c7]/5 text-[#0284c7] px-3 py-1.5 rounded-md">{article.tag}</span>
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <Clock className="w-3 h-3" />
                          {article.readTime} min
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0284c7] transition-colors leading-snug">{article.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed mb-5">{article.desc}</p>
                      <span className="inline-flex items-center gap-1.5 text-[#0284c7] font-bold text-xs uppercase tracking-widest group-hover:gap-2.5 transition-all">
                        <BookOpen className="w-3.5 h-3.5" />
                        Lees artikel
                        <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {!hasResults && (
              <div className="rounded-2xl border border-gray-100 bg-gray-50 px-6 py-12 text-center">
                <p className="text-gray-700 font-medium mb-2">Geen artikelen gevonden.</p>
                <p className="text-gray-500 text-sm mb-5">Probeer een andere categorie of zoekterm.</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-[#0284c7] text-sm font-semibold hover:underline"
                >
                  Toon alle artikelen
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
